import store from '@state/store'

export default [
  {
    path: '/',
    name: 'home',
    component: () => lazyLoadView(import('@views/home.vue')),
    meta: {
      title: 'Home',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/login.vue')),
    meta: {
      title: 'Login',
      public: true,
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/isUserLoggedIn']) {
          // Redirect to the home page instead
          next({ name: 'home' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/location/restrictions',
    name: 'location-restrictions',
    component: () => lazyLoadView(import('@views/location-restrictions.vue')),
    meta: {
      title: 'Location Restrictions',
    },
  },
  {
    path: '/message',
    name: 'message',
    component: () => lazyLoadView(import('@views/message.vue')),
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    meta: {
      title: 'Messages',
    },
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => lazyLoadView(import('@views/profile.vue')),
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
  {
    // IMPROVE: Should follow pluralised resource naming.
    //          Due to heavy embedding within RR, there is some risk changing
    //          these names.
    path: '/candidate',
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'guarantees',
        name: 'candidate-guarantees',
        component: () =>
          lazyLoadView(import('@/router/views/candidate/guarantees.vue')),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: ':candidateId/user-management-panel',
        name: 'candidate-user-management-panel',
        component: () =>
          lazyLoadView(
            import('@/router/views/candidate/user-management-panel.vue')
          ),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'devices/:candidateId',
        name: 'candidate-registered-devices',
        component: () =>
          lazyLoadView(
            import('@/router/views/candidate/registered-devices.vue')
          ),
        props: true,
      },
      {
        path: 'comments/:candidateId',
        name: 'candidate-comments',
        component: () =>
          lazyLoadView(import('@/router/views/candidate/comments.vue')),
        props: true,
      },
      {
        path: ':candidateId/bullhorn',
        name: 'candidate-bullhorn',
        component: () =>
          lazyLoadView(import('@/router/views/candidate/legacy-bullhorn.vue')),
        props: (route) => ({
          candidateId: route.params.candidateId,
          ...route.query,
        }),
        meta: {
          title: 'Bullhorn Legacy Candidate Data',
        },
      },
      {
        path: ':candidateId/forms',
        name: 'candidate-forms',
        props: (route) => ({
          candidateId: route.params.candidateId,
        }),
        component: () =>
          lazyLoadView(import('@/router/views/candidate/forms.vue')),
        children: [
          {
            path: 'interview/:registrationId',
            name: 'candidate-interview-form',
            props: true,
            component: () =>
              lazyLoadView(
                import(
                  '@/components/candidate/forms/candidate-interview-form.vue'
                )
              ),
          },
        ],
      },
    ],
  },
  {
    path: '/clients',
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: ':clientId/work-schedule',
        name: 'client-work-schedule',
        component: () =>
          lazyLoadView(import('@/router/views/client/work-schedule.vue')),
        props: (route) => ({
          clientId: route.params.clientId,
          ...route.query,
        }),
        meta: {
          title: 'Work Schedule',
        },
      },
      {
        path: ':clientId/bullhorn',
        name: 'client-bullhorn',
        component: () =>
          lazyLoadView(
            import('@/router/views/client/client-legacy-bullhorn.vue')
          ),
        props: (route) => ({
          clientId: route.params.clientId,
          ...route.query,
        }),
        meta: {
          title: 'Bullhorn Legacy Client Data',
        },
      },
    ],
  },
  {
    path: '/client/:clientId/sub-bubble-agreements',
    name: 'client-sub-bubble-agreements',
    component: () =>
      lazyLoadView(import('@/router/views/client/sub-bubble-agreements.vue')),
    props: true,
  },
  {
    path: '/client-group/:clientGroupId/sub-bubble-agreements',
    name: 'client-group-sub-bubble-agreements',
    component: () =>
      lazyLoadView(
        import('@/router/views/clientGroup/sub-bubble-agreements.vue')
      ),
    props: true,
  },
  {
    path: '/finance/overdueInvoices',
    name: 'finance-overdueInvoices',
    component: () =>
      lazyLoadView(import('@/router/views/finance/overdue-invoices.vue')),
    meta: {
      title: 'Overdue Invoices',
    },
  },
  {
    path: '/releases',
    name: 'releaseNotes',
    component: () => lazyLoadView(import('@views/Releases/releases.vue')),
    meta: {
      title: 'Release Notes',
    },
  },
  {
    path: '/profile/:username',
    name: 'username-profile',
    component: () => lazyLoadView(import('@views/profile.vue')),
    meta: {
      // HACK: In order to share data between the `beforeResolve` hook
      // and the `props` function, we must create an object for temporary
      // data only used during route resolution.
      tmp: {},
      beforeResolve(routeTo, routeFrom, next) {
        store
          // Try to fetch the user's information by their username
          .dispatch('users/fetchUser', { username: routeTo.params.username })
          .then((user) => {
            // Add the user to `meta.tmp`, so that it can
            // be provided as a prop.
            routeTo.meta.tmp.user = user
            // Continue to the route.
            next()
          })
          .catch(() => {
            // If a user with the provided username could not be
            // found, redirect to the 404 page.
            next({
              name: '404',
              params: [{ resource: 'User' }, routeTo.path],
              replace: true,
            })
          })
      },
    },
    // Set the user from the route params, once it's set in the
    // beforeResolve route guard.
    props: (route) => ({ user: route.meta.tmp.user }),
  },
  {
    path: '/bookings',
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: 'disputes',
        name: 'booking-disputes',
        component: () =>
          lazyLoadView(import('@/router/views/disputes/disputes.vue')),
        meta: {
          title: 'Booking Disputes',
        },
      },
      {
        path: 'disputes/v2',
        name: 'booking-disputes-v2',
        component: () =>
          lazyLoadView(import('@/router/views/disputes/disputes-v2.vue')),
        meta: {
          title: 'Booking Disputes',
        },
      },
    ],
  },
  {
    path: '/forms',
    component: {
      render(c) {
        return c('router-view')
      },
    },
    children: [
      {
        path: ':passkey',
        name: 'view-form-submissions',
        component: () =>
          lazyLoadView(import('@/router/views/forms/view-form-submission.vue')),
        meta: {
          title: 'View Form Submission',
        },
      },
    ],
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut')
        const authRequiredOnPreviousRoute = routeFrom.matched.some(
          (route) => route.meta.authRequired
        )
        // Navigate back to previous page, or home as a fallback
        next(authRequiredOnPreviousRoute ? { name: 'home' } : { ...routeFrom })
      },
    },
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/_404.vue').default,
    meta: {
      public: true,
    },
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
    meta: {
      public: true,
    },
  },
  {
    path: '/500',
    redirect: { name: 'ErrorPage' },
    meta: { public: true, label: 'Error', type: '500' },
  },
  {
    path: '/401',
    redirect: { name: 'UnauthorizedPage' },
    meta: {
      public: true,
      label: 'Unauthorized',
      type: '401',
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '/error',
    redirect: { name: 'ErrorPage' },
    meta: { public: true, label: 'Error', type: '500' },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: 'ErrorPage',
    meta: { public: true, label: 'Error', type: '500' },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: 'UnauthorizedPage',
    meta: {
      public: true,
      label: 'Unauthorized',
      type: '401',
      icon: 'mdi-account-cancel',
    },
  },
  {
    path: '*',
    component: require('@views/_error.vue').default,
    props: true,
    name: 'ActionLocked',
    meta: {
      public: true,
      label: 'Action Locked',
      type: '117',
      icon: 'mdi-shield-alert',
    },
  },
]

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading.vue').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout.vue').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}
